// Pipelines: the tenant's list, and the admin writes.  tickets[9304]
//
// Reads embed each pipeline's steps and the departments that chose it, so one
// query gives every surface — settings, the ticket stepper, the list's
// progress circle — the whole shape. Writes go straight to the tables (RLS:
// admins only); steps are saved as a whole list through set_pipeline_steps,
// the only way to reorder them without colliding with their (pipeline,
// position) uniqueness, and a department's pipelines and default through
// set_subsheet_pipelines (0060).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/db/client";
import { toPipeline, type Pipeline } from "@/lib/pipelines";
import type { PipelineDepartmentChange } from "@/lib/departmentAssignments";

export const PIPELINES_KEY = ["pipelines"] as const;

/**
 * The tenant's pipelines with their steps and department choices. Kept fresh
 * by useGlobalPipelinesSync, mounted once in AppContent, so the several
 * components that read this share one subscription.
 */
export const usePipelines = () =>
  useQuery<Pipeline[]>({
    queryKey: PIPELINES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pipelines")
        .select(
          "id, name, description, order_index, pipeline_steps(status_id, position), pipeline_subsheets(subsheet_id)",
        )
        .order("order_index", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map(toPipeline);
    },
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

/**
 * Every department's status allow-list, for the editor's "this step is hidden
 * in HR" warning. Small: one row per (department, allowed status).
 */
export const useAllSubsheetStatuses = () =>
  useQuery({
    queryKey: ["subsheet-statuses", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("subsheet_statuses").select("subsheet_id, status_id");
      if (error) throw error;
      return data ?? [];
    },
  });

interface PipelineDraft {
  /** Omitted to create a new pipeline. */
  id?: string;
  name: string;
  description: string;
  statusIds: string[];
}

export function usePipelineMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: PIPELINES_KEY });

  const save = useMutation({
    mutationFn: async (draft: PipelineDraft) => {
      const name = draft.name.trim();
      if (!name) throw new Error("A pipeline needs a name");
      const description = draft.description.trim() || null;

      let id = draft.id;
      if (id) {
        const { error } = await supabase.from("pipelines").update({ name, description }).eq("id", id);
        if (error) throw error;
      } else {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        // Appended after the last one. A distinct index per pipeline is what
        // lets swapOrder move it: swapping two equal indexes changes nothing.
        const { data: last, error: lastError } = await supabase
          .from("pipelines")
          .select("order_index")
          .order("order_index", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (lastError) throw lastError;
        const { data, error } = await supabase
          .from("pipelines")
          .insert({ name, description, created_by_id: user.id, order_index: (last?.order_index ?? -1) + 1 })
          .select("id")
          .single();
        if (error) throw error;
        id = data.id;
      }

      const steps = await supabase.rpc("set_pipeline_steps", { _pipeline_id: id, _status_ids: draft.statusIds });
      if (steps.error) {
        // A new pipeline whose steps failed would linger stepless and block a
        // retry under the same name, so take it back out.
        if (!draft.id) {
          const undo = await supabase.from("pipelines").delete().eq("id", id);
          if (undo.error)
            throw new Error(`${steps.error.message} (and "${name}" was created without steps; delete it)`);
        }
        throw steps.error;
      }
    },
    onSuccess: (_data, draft) => {
      invalidate();
      toast.success(draft.id ? "Pipeline updated" : "Pipeline added");
    },
    onError: (e: Error) => toast.error(e.message || "Could not save the pipeline"),
  });

  // Swap two pipelines' order_index to move one up or down, as statuses do.
  const swapOrder = useMutation({
    mutationFn: async ([a, b]: [Pipeline, Pipeline]) => {
      const results = await Promise.all([
        supabase.from("pipelines").update({ order_index: b.order_index }).eq("id", a.id),
        supabase.from("pipelines").update({ order_index: a.order_index }).eq("id", b.id),
      ]);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message || "Could not reorder"),
  });

  // Tickets and departments on it are cleared by ON DELETE SET NULL; their
  // statuses are untouched.
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pipelines").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
      toast.success("Pipeline deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete the pipeline"),
  });

  return { save, swapOrder, remove };
}

/**
 * Saves departments' pipelines and defaults (#9304, department-side since
 * 0060): one set_subsheet_pipelines call per change, in order — top-down for
 * a whole tree from the Pipelines page (pipelineDepartmentChanges), or a
 * single department from Departments -> Manage pipelines
 * (departmentPipelinesPayload). Callers say what saved.
 */
export function useSetPipelineDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: readonly PipelineDepartmentChange[]) => {
      for (const { subsheetId, pipelineIds, defaultId } of changes) {
        const { error } = await supabase.rpc("set_subsheet_pipelines", {
          _subsheet_id: subsheetId,
          _pipeline_ids: pipelineIds,
          _default_pipeline_id: defaultId,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIPELINES_KEY });
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
    },
  });
}

/** How many live tickets and departments a delete would detach — for the confirm dialog. */
export async function countPipelineUsage(id: string): Promise<{ tickets: number; departments: number }> {
  const [tickets, departments] = await Promise.all([
    supabase
      .from("backlog_items")
      .select("id", { count: "exact", head: true })
      .eq("pipeline_id", id)
      .is("deleted_at", null),
    supabase.from("subsheets").select("id", { count: "exact", head: true }).eq("default_pipeline_id", id),
  ]);
  if (tickets.error) throw tickets.error;
  if (departments.error) throw departments.error;
  return { tickets: tickets.count ?? 0, departments: departments.count ?? 0 };
}
